import type { ImportTag } from "@nu-assist/contracts";
/**
 * ImportsToolbar — the card-panel header (R-F1): title + count badge, search
 * box (debounced → `q`), a searches typeahead that applies one search as a
 * filter, multi-select filter chips (→ a repeated `tag`), and the "Imported by:" scope
 * dropdown. An optional `headerAction` slot (next to the scope dropdown) renders
 * the "+ New Import" CTA (subtask 03). All copy English; design-system tokens only.
 */
import type { ReactNode } from "react";
import { Search } from "lucide-react";

import type { ImportsClient } from "../../../clients/imports.client";
import { PickerSearchFilterInput } from "../../../features/imports/selection/PickerSearchFilterInput";
import { ImportOwnerMenu, type OwnerOption } from "./ImportOwnerMenu";
import { FILTER_CHIPS } from "./tagDefs";
import { VocabularyPopover } from "./VocabularyPopover";

export interface ImportsToolbarProps {
  total: number;
  /** Raw (un-debounced) search text the input shows. */
  searchText: string;
  onSearchTextChange: (text: string) => void;
  /**
   * The selected tag chips. Multi-select with OR semantics: `[bct, ai]` shows
   * the imports carrying EITHER tag. An EMPTY array is the "All" state — no
   * filter, everything shown — so there is no third "nothing is selected and
   * nothing is shown" reading for a reader to fall into.
   */
  activeTags: readonly ImportTag[];
  /**
   * A chip was clicked: its value, or `null` for "All".
   *
   * Reports WHICH chip moved rather than the new selection, deliberately. The
   * toggle arithmetic then lives in exactly one place (`ImportsListView`),
   * beside the page reset it has to happen with — a toolbar that computed the
   * set would be a second place for it to disagree with the query key.
   */
  onTagsChange: (tag: ImportTag | null) => void;
  owner: string;
  meEmail: string;
  /** `null` until the owners request answers — see `ImportOwnerMenu`. */
  owners: readonly OwnerOption[] | null;
  onOwnerChange: (owner: string) => void;
  /** Optional header-row action (e.g. the "+ New Import" CTA). */
  headerAction?: ReactNode;
  /**
   * The title over the list — "People Lists" on both surfaces (it was
   * "Contact Lists" until Trello #729 cl.1). The Search Detail tab used to
   * pass "Contacts" against a tab reading "List of contacts", leaving the
   * reader to work out that the two named the same rows; since Trello #711
   * cl.1 the tab, the panel and the nav all say the same word.
   */
  title?: string;
  /**
   * Semantic level of the title. `h1` on its own page; `h2` when
   * embedded under a page that already has one (the Search Detail contacts
   * tab), where a second `h1` would misdescribe the document outline. Visual
   * size follows the level, so the embedded copy also reads as the subordinate
   * heading it is.
   */
  headingLevel?: "h1" | "h2";
  /**
   * The searches typeahead, or omitted to leave it out entirely.
   *
   * ONE optional object rather than three optional props, because the control
   * needs all three and is useless with a subset: a caller that passed only the
   * callback used to lose the typeahead silently, with nothing on screen and no
   * type error to say why. Grouping them makes half a configuration
   * unrepresentable instead of merely wrong.
   *
   * Its PRESENCE is what renders the control, exactly as `onClearSearchFilter`'s
   * presence is what renders the chip one layer up: the Search Detail contacts
   * tab PINS its filter to the search whose page it is, so a control that could
   * point the list at a different search would make the tab lie about its own
   * contents. Field names match `PickerSearchFilterInput`'s own props so the
   * hand-off is a pass-through and not a rename.
   */
  searchFilter?: {
    /** Typed client the typeahead queries. */
    client: ImportsClient;
    /** The currently applied narrowing — resets the typeahead's input after a
     *  pick (see `PickerSearchFilterInput`). */
    appliedSearchId: string | null;
    /** Apply (or replace) the search narrowing. */
    onApply: (searchId: string, searchName: string | null) => void;
  };
}

export function ImportsToolbar({
  total,
  searchText,
  onSearchTextChange,
  activeTags,
  onTagsChange,
  owner,
  meEmail,
  owners,
  onOwnerChange,
  headerAction,
  title = "People Lists",
  headingLevel = "h1",
  searchFilter,
}: ImportsToolbarProps) {
  const Heading = headingLevel;
  return (
    <div className="flex flex-col gap-3" data-testid="imports-toolbar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <Heading
            className={headingLevel === "h1" ? "text-h1 text-text" : "text-h2 text-text"}
          >
            {title}
          </Heading>
          <span className="text-label text-text-muted" data-testid="imports-count">
            ({total.toLocaleString()})
          </span>
          {/* The rename made "person" vs "candidate" load-bearing, so the
              glossary lives where the reader meets both words rather than on
              a settings page nobody opens. */}
          <VocabularyPopover />
        </div>
        <div className="flex items-center gap-3">
          <ImportOwnerMenu
            value={owner}
            meEmail={meEmail}
            owners={owners}
            onChange={onOwnerChange}
          />
          {/* No reviewer menu: a derived review file is OWNED by the
              reviewer, so `Imported by` already answers "reviewed by whom"
              and "reviewed by me" is `owner=you`. BCT settled the same
              question the same way (PR #646). */}
          {headerAction}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-60">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted"
            aria-hidden="true"
          />
          <input
            type="search"
            value={searchText}
            onChange={(e) => onSearchTextChange(e.target.value)}
            placeholder="Search by people list title or search name"
            aria-label="Search by people list title or search name"
            data-testid="imports-search"
            className="w-full rounded-md border border-border bg-surface py-1.5 pl-9 pr-3 text-body text-text placeholder:text-text-muted hover:border-border-hover focus:border-border-hover focus:outline-none"
          />
        </div>

        {/* The SECOND search box in this row, and not a duplicate of the first:
            the one above matches IMPORTS (filename / cached search name), this
            one matches SEARCHES and applies one as a filter. Reused wholesale
            from the BCT / AI Review finder — `PickerSearchFilterInput` already
            owns the DTO mapping and the "pure filter" shape (`value={null}` +
            a remount key), so the two surfaces cannot render the same search
            differently. The applied narrowing is shown by `SearchFilterChip`,
            which is why this control never renders a selected summary.

            The visible "Filter by search:" label is load-bearing, not
            decoration. Two grey boxes with magnifier icons sit side by side in
            this row, and the FIRST one's placeholder says "…or search name" —
            truthfully, since `q` matches `search_name_cached` too — which
            leaves a sighted reader with two boxes that both claim to be about
            searches. The `aria-label` covers screen readers; this covers
            everyone else. Labelling the second box rather than editing the
            first is deliberate: the first placeholder is accurate, and this
            control's own placeholder is shared with the BCT / AI Review
            finder, where there is no adjacent box to be confused with. */}
        {searchFilter ? (
          <div className="flex items-center gap-2">
            <span className="whitespace-nowrap text-label text-text-secondary">
              Filter by search:
            </span>
            <PickerSearchFilterInput
              client={searchFilter.client}
              appliedSearchId={searchFilter.appliedSearchId}
              onApply={searchFilter.onApply}
            />
          </div>
        ) : null}

        {/* Native <fieldset> carries the group semantics biome's
            useSemanticElements prefers over role="group"; reset to inline-flex
            so it lays out like the prototype's chip row. */}
        <fieldset
          className="flex min-w-0 flex-wrap items-center gap-1.5 border-0 p-0 m-0"
          data-testid="imports-chips"
        >
          <legend className="sr-only">Filter by type</legend>
          {FILTER_CHIPS.map((chip) => {
            // "All" is active when NOTHING is selected, which is the same state
            // — the chip is a shortcut for clearing, not a seventh tag. Every
            // other chip is active when it is IN the selection, so two can be
            // lit at once and neither of them is "All".
            const active =
              chip.value === null ? activeTags.length === 0 : activeTags.includes(chip.value);
            return (
              <button
                key={chip.label}
                type="button"
                aria-pressed={active}
                data-chip={chip.value ?? "all"}
                onClick={() => onTagsChange(chip.value)}
                className={
                  active
                    ? "rounded-full border border-accent bg-accent-muted px-3 py-1 text-label text-accent"
                    : "rounded-full border border-border bg-surface px-3 py-1 text-label text-text-secondary hover:border-border-hover"
                }
              >
                {chip.label}
              </button>
            );
          })}
        </fieldset>
      </div>
    </div>
  );
}
